import {
  BehaviorSubject,
  distinctUntilChanged,
  ReplaySubject,
  tap,
} from 'rxjs';
import { BdUserConsent } from '../user-consent/user-consent';
import { BdUserConsentService } from '../user-consent/user-consent.service';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import { AccountInfo } from '@azure/msal-browser';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BdUserTrackingService } from '../tracking/tracking.service';

@Injectable({
  providedIn: 'root',
})
export class BdCurrentUserService {
  public consent$ = new ReplaySubject<BdUserConsent>();

  public authenticated$ = new BehaviorSubject<boolean>(false);

  public currentUser = new ReplaySubject<AccountInfo | null>();

  public accountInfo: AccountInfo | null = null;

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _authenticationService = inject(BdAuthenticationService);
  private readonly _userConsentService = inject(BdUserConsentService);
  private readonly _userTrackingService = inject(BdUserTrackingService);

  public init(): void {
    this._authenticationService.authentication$
      .pipe(
        tap((authenticated) => {
          if (!authenticated) {
            this.accountInfo = null;
          }

          this._onAuthenticatedChange(authenticated);
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((authenticated) => {
        this.authenticated$.next(authenticated);
      });

    this._userConsentService.userConsent$
      .pipe(
        distinctUntilChanged((previous, current) => previous.equals(current)),
        tap((consentData) => {
          console.debug('onConsentChange', consentData);
          this._onConsentChange(consentData);
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((consent) => {
        this.consent$.next(consent);
      });

    this._authenticationService.accountInfo$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((accountInfo) => {
        this.currentUser.next(accountInfo);
        this.accountInfo = accountInfo;
      });
  }

  private _onConsentChange(data: BdUserConsent): void {
    if (data.consent.tracking) {
      this._userTrackingService.init(this.accountInfo);
    } else {
      this._userTrackingService.destroy();
    }
  }

  private _onAuthenticatedChange(authenticated: boolean): void {
    if (authenticated) {
      this._userTrackingService.addAuthenticatedUserContext(this.accountInfo);
    } else {
      this._userTrackingService.removeAuthenticatedUserContext();
    }
  }
}
